/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 뷰 패널 클래스를 정의한다.
 *
 * @file /src/components/viewer/View.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 6. 24.
 */
import * as parent from '@components/panel/Panel';
import { Pagination } from '@components/pagination/Pagination';
import { Viewer } from '@components/viewer/Viewer';
import { List } from '@components/viewer/List';
import { Panel } from '@components/viewer/Panel';
import { Html } from '@utils/Html';
import { Language } from '@utils/Language';
import { Format } from '@utils/Format';
import './View.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 그리드
     */
    list: List;

    /**
     * 상세패널
     */
    panel?: Panel;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class View extends parent.Panel {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'view';

    /**
     * 뷰 패널의 기본주소
     */
    baseUrl: string | null;

    /**
     * 뷰 패널 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(
        public properties: Pick<
            Properties<View>,
            'id' | 'name' | 'icon' | 'title' | 'list' | 'panel' | 'resizeNm'
        > = null
    ) {
        super({
            ...properties,
            title: properties?.title ?? Language.get('components.viewer.all'),
            layout: 'column',
            items: [properties.list, properties.panel ?? null],
            topbar: properties.list.properties?.topbar ?? null,
            bottombar: new Pagination({
                type: 'default',
                store: properties.list.getStore(),
                tools: properties.list.properties?.bottombar ?? null,
            }),
            listeners: {
                resize: (me) => {
                    const grid = me.getItemAt<List>(0);
                    const panel = me.getItemAt<Panel>(1) ?? null;
                    if (panel !== null) {
                        const $header = grid.getHeader().$getHeader();
                        const freeze = Html.get('div[data-model=table][data-role=freeze]', $header).getWidth();
                        const column = Html.get(
                            'div[data-model=table][data-role=column][data-index="0"]',
                            $header
                        ).getWidth();
                        panel.setMaxWidth(me.$getComponent().getWidth() - Math.max(freeze, column) - 100);
                    }
                },
            },
        });

        this.resizeNm = properties.resizeNm ?? null;
        if (this.resizeNm !== null) {
            this.setResizeNm(this.resizeNm);
        }
    }

    /**
     * 뷰 기본 URL 을 가져온다.
     *
     * @param is_base - 뷰 기본 URL 을 가져올지 여부
     * @returns 기본 URL
     */
    async getUrl(is_base: boolean = false): Promise<string | null> {
        if (this.baseUrl === undefined) {
            this.baseUrl = Format.rtrim((await this.getTask()?.getUrl(true)) ?? null, '/');
            if (this.baseUrl === null) {
                return null;
            }

            const parent = this.getParent();
            if (parent?.variant == 'viewer' || parent?.variant == 'tab') {
                this.baseUrl += '/' + this.name;
            }
        }

        if (is_base === true) {
            return this.baseUrl;
        } else {
            const selections = this.getList().getSelection().getSelections();
            if (selections.length == 0) {
                return this.baseUrl;
            } else {
                return this.baseUrl + '/' + this.getList().getRecordUrl(selections[0]);
            }
        }
    }

    /**
     * 뷰 기본 URL 을 제외한 경로의 특정 인덱스의 값을 가져온다.
     *
     * @param index - 경로 인덱스
     * @returns path
     */
    async getPathAt(index: number): Promise<string | null> {
        const baseUrl = await this.getUrl(true);
        if (baseUrl === null) {
            return null;
        }

        const currentUrl = (await this.getTask().getUrl()) ?? null;
        const paths = Format.trim(currentUrl.replace(new RegExp(`^${baseUrl}/?`), ''), '/')
            .split('/')
            .filter((value) => (value?.length > 0 ? value : null));

        return paths[index] ?? null;
    }

    /**
     * 뷰 기본 URL 에 추가경로를 설정한다.
     *
     * @params path
     */
    async setPath(path: string | null): Promise<void> {
        const baseUrl = await this.getUrl(true);
        if (baseUrl === null) {
            return;
        }

        if (path === null) {
            await this.getTask()?.setUrl(baseUrl);
        } else {
            path = Format.trim(path, '/');
            await this.getTask()?.setUrl(baseUrl + '/' + path);
        }
    }

    /**
     * 뷰어를 가져온다.
     *
     * @returns 뷰어
     */
    getViewer(): Viewer | null {
        if (this.getParent()?.variant == 'viewer') {
            return this.getParent() as Viewer;
        }

        return null;
    }

    /**
     * 상세패널 크기조절 저장명을 설정한다.
     *
     * @param resizeNm - 상세패널 크기조절 저장명
     */
    setResizeNm(resizeNm: string): void {
        this.getPanel()?.setResizeNm(resizeNm);
    }

    /**
     * 목록패널을 가져온다.
     *
     * @returns 목록패널
     */
    getList(): List {
        return this.getItemAt(0);
    }

    /**
     * 상세패널을 가져온다.
     *
     * @returns 목록패널
     */
    getPanel(): Panel | null {
        return this.getItemAt(1) ?? null;
    }
}
